"use client";

import React from "react";
import { Phone, Shield, Clock, Star, Loader, CircleCheck, CircleAlert } from "lucide-react";
import { FormProvider, useWvcForm } from "@/integrations/forms/FormProvider";
import { Form, FormField, FormItem, FormLabel, FormControl, FormMessage, FormDescription } from "@/components/ui/form";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Link } from "@/components/common/Link";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
function CompanionForm() {
  const {
    isSubmitting,
    isSubmitted,
    submitError,
    successMessage,
    handleSubmit,
    resetForm,
    control
  } = useWvcForm();
  if (isSubmitted && !submitError) {
    return <div className="flex flex-col items-center justify-center py-12 text-center space-y-6 animate-in fade-in zoom-in duration-500">
        <div className="size-16 rounded-md bg-primary/10 flex items-center justify-center mb-4">
          <CircleCheck className="size-8 text-primary" />
        </div>
        <h3 className="text-2xl font-default font-bold text-foreground">Tour Scheduled</h3>
        <p className="text-muted-foreground max-w-md">
          {successMessage}
        </p>
        <Button onClick={resetForm} variant="outline" className="mt-8 border-primary/50 hover:bg-primary/5 rounded-md">
          Schedule Another Visit
        </Button>
      </div>;
  }
  return <Form onSubmit={handleSubmit} className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="full_name" rules={{
        required: "Full Name is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Full Name</FormLabel>
              <FormControl>
                <Input placeholder="John Smith" className="bg-background border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-slate-500">Enter your legal name for the visitor log.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="email" rules={{
        required: "Email is required",
        pattern: {
          value: /^[^\s@]+@[^\s@]+\.[^\s@]+$/,
          message: "Invalid email address"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Email Address</FormLabel>
              <FormControl>
                <Input type="email" placeholder="john@urbanliving.com" className="bg-background border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-slate-500">We'll send tour confirmation to this address.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="phone" rules={{
        required: "Phone Number is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Phone Number</FormLabel>
              <FormControl>
                <Input type="tel" placeholder="(555) 000-0000" className="bg-background border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-slate-500">For day-of-tour coordination.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="occasion" rules={{
        required: "Please select a property"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Preferred Residence</FormLabel>
              <Select onValueChange={field.onChange} defaultValue={field.value}>
                <FormControl>
                  <SelectTrigger className="bg-background border-border focus-visible:ring-primary w-full rounded-md">
                    <SelectValue placeholder="Select a property" />
                  </SelectTrigger>
                </FormControl>
                <SelectContent>
                  {["The Skyline Penthouse", "Modern Loft A", "Garden Suite", "Urban Studio", "Executive 2-Bedroom"].map((option, i) => <SelectItem key={i} value={option} data-index={i}>
                      {option}
                    </SelectItem>)}
                </SelectContent>
              </Select>
              <FormDescription className="text-xs text-slate-500">Choose the unit you'd like to view.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
        <FormField control={control} name="event_date" rules={{
        required: "Tour Date is required"
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Tour Date</FormLabel>
              <FormControl>
                <Input type="date" className="bg-background border-border focus-visible:ring-primary block w-full rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-slate-500">Select your preferred visiting day.</FormDescription>
              <FormMessage />
            </FormItem>} />

        <FormField control={control} name="duration_hours" rules={{
        required: "Duration is required",
        min: {
          value: 1,
          message: "Minimum 1 hour"
        },
        max: {
          value: 24,
          message: "Maximum 24 hours"
        }
      }} render={({
        field
      }) => <FormItem>
              <FormLabel className="text-foreground/80 font-medium">Estimated Duration (Mins)</FormLabel>
              <FormControl>
                <Input type="number" min="15" max="120" placeholder="e.g., 30" className="bg-background border-border focus-visible:ring-primary rounded-md" {...field} />
              </FormControl>
              <FormDescription className="text-xs text-slate-500">How much time you'd like for the tour.</FormDescription>
              <FormMessage />
            </FormItem>} />
      </div>

      <FormField control={control} name="additional_details" render={({
      field
    }) => <FormItem>
            <FormLabel className="text-foreground/80 font-medium">Special Requirements</FormLabel>
            <FormControl>
              <Textarea placeholder="Tell us about your move-in timeline or specific amenities you're looking for..." className="resize-none bg-background border-border focus-visible:ring-primary min-h-[120px] rounded-md" {...field} />
            </FormControl>
            <FormDescription className="text-xs text-slate-500">Optional: let us know if you have pets or need parking details.</FormDescription>
            <FormMessage />
          </FormItem>} />

      {submitError && <div className="p-4 bg-destructive/10 border border-destructive/20 rounded-md flex items-start gap-3">
          <CircleAlert className="size-5 text-destructive shrink-0 mt-0.5" />
          <p className="text-sm text-destructive-foreground">{submitError}</p>
        </div>}

      <Button type="submit" disabled={isSubmitting} className="w-full h-12 text-base font-default font-bold bg-primary text-primary-foreground hover:bg-primary/90 transition-all shadow-md hover:shadow-lg rounded-md">
        {isSubmitting ? <>
            <Loader className="animate-spin mr-2 size-5" />
            Scheduling Tour...
          </> : 'Book Your Private Tour'}
      </Button>
      
      <p className="text-center text-xs text-slate-500 mt-4 flex items-center justify-center gap-1.5">
        <Shield className="size-3.5" />
        Your privacy is our priority. Data is handled with absolute discretion.
      </p>
    </Form>;
}
export default function BookYourTour() {
  const {
    ref: sectionRef,
    isVisible: isSectionVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  const {
    ref: formRef,
    isVisible: isFormVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  return <section data-section-id="241608" id="book-your-tour" className="bg-background text-foreground py-24 lg:py-32 relative overflow-hidden" ref={sectionRef}>
      {}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute top-[-10%] left-[-10%] w-[40%] h-[40%] rounded-md bg-primary/5 blur-[120px]" />
        <div className="absolute bottom-[-10%] right-[-10%] w-[40%] h-[40%] rounded-md bg-primary/5 blur-[120px]" />
      </div>

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-8 items-center">
          
          {}
          <div className={`lg:col-span-5 space-y-8 transition-all duration-1000 ease-out ${isSectionVisible ? "opacity-100 translate-x-0" : "opacity-0 -translate-x-12"}`}>
            <div className="space-y-4">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary/10 border border-primary/20 text-primary text-sm font-default font-semibold tracking-tight">
                <Star className="size-4 fill-primary/50" />
                <span>Exclusive Access</span>
              </div>
              <h2 className="text-4xl md:text-6xl font-default font-black leading-tight text-foreground tracking-tighter">
                Book Your <br />
                <span className="text-primary">
                  Private Tour
                </span>
              </h2>
              <p className="text-lg text-slate-600 max-w-md leading-relaxed font-default">
                Discover the pinnacle of urban living. Our leasing specialists are ready to guide you through our curated collection of luxury residences.
              </p>
            </div>

            <div className="relative rounded-md overflow-hidden shadow-xl border border-border group">
              <div className="absolute inset-0 bg-gradient-to-t from-foreground/40 via-transparent to-transparent z-10" />
              <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/luxury_apartment_lobby.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/luxury_apartment_lobby.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1686159/img/luxury_apartment_lobby.png-wvc-sizes" alt="Urban Living Rentals Lobby" className="w-full h-[300px] object-cover transition-transform duration-700 group-hover:scale-105" loading="lazy" />
              
              <div className="absolute bottom-0 left-0 w-full p-6 z-20">
                <div className="backdrop-blur-md bg-white/90 border border-border rounded-md p-5 flex items-center gap-4 shadow-md">
                  <div className="size-12 rounded-md bg-primary/10 flex items-center justify-center shrink-0">
                    <Phone className="size-5 text-primary" />
                  </div>
                  <div>
                    <p className="text-sm text-slate-500 font-medium mb-1">Leasing Concierge</p>
                    <Link to="tel:+15559876543" className="text-xl font-default font-bold text-foreground hover:text-primary transition-colors">
                      +1 (555) 987-6543
                    </Link>
                  </div>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-2 gap-4 pt-4">
              <div className="flex items-center gap-3">
                <Clock className="size-5 text-primary" />
                <span className="text-sm text-slate-600 font-medium">Daily Viewings</span>
              </div>
              <div className="flex items-center gap-3">
                <Shield className="size-5 text-primary" />
                <span className="text-sm text-slate-600 font-medium">Verified Listings</span>
              </div>
            </div>
          </div>

          {}
          <div ref={formRef} className={`lg:col-span-7 transition-all duration-1000 delay-200 ease-out ${isFormVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12"}`}>
            <div className="bg-white border border-border rounded-md p-6 sm:p-10 shadow-xl relative">
              {}
              <div className="absolute top-0 left-0 w-16 h-16 border-t-2 border-l-2 border-primary/20 rounded-tl-md pointer-events-none" />
              <div className="absolute bottom-0 right-0 w-16 h-16 border-b-2 border-r-2 border-primary/20 rounded-br-md pointer-events-none" />
              
              <div className="mb-8">
                <h3 className="text-2xl font-default font-bold text-foreground mb-2">Visit Details</h3>
                <p className="text-sm text-slate-500 font-default">Please provide your preferences to help us prepare the perfect viewing experience.</p>
              </div>

              <FormProvider formKey="tour_booking_form_schedule_a_visit" formId="tour_booking_form_schedule_a_visit" sectionName="Book Your Tour" defaultValues={{
              full_name: "",
              email: "",
              phone: "",
              occasion: "",
              event_date: "",
              duration_hours: "",
              additional_details: ""
            }} validationRules={{
              full_name: {
                required: true
              },
              email: {
                required: true,
                pattern: "email"
              },
              phone: {
                required: true
              },
              occasion: {
                required: true
              },
              event_date: {
                required: true
              },
              duration_hours: {
                required: true,
                min: 1,
                max: 24
              }
            }} submitText="Book Your Private Tour" successMessage="Your tour request has been received. Our leasing team will contact you shortly to confirm your appointment." errorMessage="There was an error scheduling your tour. Please try again or call our leasing office directly.">
                <CompanionForm />
              </FormProvider>
            </div>
          </div>

        </div>
      </div>
    </section>;
}